import { useState, useRef, useEffect, useCallback } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { cn } from '@/utils/cn';

/**
 * Combobox Component
 *
 * Autocomplete input with suggestions dropdown
 * Supports free-form input and selection from list
 */

export interface ComboboxOption {
  value: string;
  label: string;
  count?: number;
}

interface ComboboxProps {
  value: string;
  onChange: (value: string) => void;
  options: ComboboxOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  required?: boolean;
  emptyMessage?: string;
  loading?: boolean;
}

export const Combobox = ({
  value,
  onChange,
  options,
  placeholder = 'Type or select...',
  disabled = false,
  className,
  id,
  required,
  emptyMessage = 'No suggestions found',
  loading = false,
}: ComboboxProps) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const justSelectedRef = useRef(false);

  // Filter options based on input
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(inputValue.toLowerCase())
  );

  // Sync input value with external value
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Reset highlight when options change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [inputValue]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('[data-combobox-item]');
      const item = items[highlightedIndex] as HTMLElement;
      if (item) {
        item.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
    if (!open) setOpen(true);
  };

  const handleSelect = useCallback(
    (option: ComboboxOption) => {
      setInputValue(option.value);
      onChange(option.value);
      setOpen(false);
      justSelectedRef.current = true; // Prevent handleFocus from reopening
      inputRef.current?.focus();
    },
    [onChange]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        break;
      case 'Tab':
        setOpen(false);
        break;
    }
  };

  const handleFocus = () => {
    // Skip opening if we just selected an option (focus was restored after selection)
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    // Always open on focus - content will show loading, options, or empty message
    setOpen(true);
  };

  const handleBlur = (e: React.FocusEvent) => {
    // Don't close if clicking inside the popover
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (relatedTarget?.closest('[data-combobox-content]')) {
      return;
    }
    // Small delay to allow click events to fire
    setTimeout(() => setOpen(false), 150);
  };

  return (
    <Popover.Root
      open={open}
      onOpenChange={(newOpen) => {
        // Prevent Radix from closing when input still has focus
        if (!newOpen && document.activeElement === inputRef.current) {
          return;
        }
        setOpen(newOpen);
      }}
    >
      <Popover.Anchor asChild>
        <div className="relative">
          <input
            ref={inputRef}
            id={id}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            autoComplete="off"
            role="combobox"
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-autocomplete="list"
            className={cn(
              'w-full px-4 py-2.5 text-[16px] lg:text-sm',
              'bg-white border border-gray-300 rounded-lg',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
              'placeholder:text-gray-400',
              'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
              'transition-colors duration-200',
              className
            )}
          />
          {/* Dropdown indicator */}
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <svg
              className={cn(
                'w-4 h-4 text-gray-400 transition-transform duration-200',
                open && 'rotate-180'
              )}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
      </Popover.Anchor>

      <Popover.Portal>
        <Popover.Content
          data-combobox-content
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
          className={cn(
            'z-tooltip', // Must be above modals (z-modal-content: 90)
            'w-[var(--radix-popover-trigger-width)]',
            'max-h-[300px] overflow-auto',
            'bg-white rounded-lg shadow-lg border border-gray-200',
            'animate-in fade-in-0 zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95'
          )}
        >
          <div ref={listRef} role="listbox" className="py-1">
            {loading ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                Loading suggestions...
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                {inputValue ? emptyMessage : 'Start typing for suggestions'}
              </div>
            ) : (
              filteredOptions.map((option, index) => (
                <button
                  key={option.value}
                  type="button"
                  data-combobox-item
                  role="option"
                  aria-selected={highlightedIndex === index}
                  onClick={() => handleSelect(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  className={cn(
                    'w-full px-4 py-2 text-left text-sm',
                    'flex items-center justify-between gap-2',
                    'transition-colors duration-100',
                    'focus:outline-none',
                    highlightedIndex === index
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-700 hover:bg-gray-50',
                    option.value.toLowerCase() === inputValue.toLowerCase() &&
                      'font-medium'
                  )}
                >
                  <span className="truncate">{option.label}</span>
                  {option.count !== undefined && (
                    <span
                      className={cn(
                        'text-xs px-1.5 py-0.5 rounded-full flex-shrink-0',
                        highlightedIndex === index
                          ? 'bg-primary-100 text-primary-600'
                          : 'bg-gray-100 text-gray-500'
                      )}
                    >
                      {option.count}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
